<script lang="ts">
	import { slideIn } from '$lib/actions/slideIn';
	import Button from '$lib/buttons/v3/button.svelte';
	import { ReactiveProduct } from '$lib/state/reactiveProduct.svelte';
	import Blok from '$lib/storyblok/blok.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import { editable } from '$lib/storyblok/editable';
	import type { SbImage, SbLink, StripeProductData } from '$lib/storyblok/types';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';

	interface Props {
		content: ISbStoryData<{
			product: StripeProductData;
			headline: string;
			cta_label: string;
			cta_link: SbLink;
			benefits: Array<
				ISbStoryData<{ icon: SbImage; label: string; description: ISbRichtext }>['content']
			>;
			image: SbImage;
			payment_messaging: ISbStoryData[];
		}>['content'];
	}

	let { content }: Props = $props();

	let product = $derived(new ReactiveProduct(content.product.priceLookupKey, content.product.data));
</script>

<section use:editable={content} class="py-5 lg:mx-4 lg:pb-15">
	<PageGrid16 class="relative mx-3 overflow-hidden rounded-3xl bg-brown-6">
		<div class="col-span-full mx-5 py-9 lg:col-start-2 lg:col-end-7 lg:pb-25 lg:pt-40">
			<h1
				class="mb-4 text-balance text-[36px]/[112%] font-semibold tracking-[-.36px] text-brown-1 lg:mb-6 lg:text-[48px]/[110%] lg:tracking-[-.48px]"
				use:slideIn
			>
				{content.headline}
			</h1>
			<div class="flex items-center gap-x-2" use:slideIn={{ delay: 80 }}>
				<Button class="btn-large" href={getSbUrl(content.cta_link)} target={content.cta_link.target}
					>{content.cta_label}</Button
				>
				{#each content.payment_messaging as blok}
					<div class="flex">
						<Blok content={blok} />
					</div>
				{/each}
			</div>
			<ul class="mt-8 flex flex-col gap-y-6 lg:mt-10 lg:gap-y-8">
				{#each content.benefits as benefit, i}
					{@const delay = (i + 2) * 40}
					<li class="flex flex-col" use:slideIn={{ delay, threshold: 0.1 }}>
						<Image
							src={benefit.icon.filename}
							alt={benefit.icon.alt}
							title={benefit.icon.title}
							class="h-6 w-6"
							height={24}
							width={24}
						/>
						<strong
							class="mb-1 mt-3 text-[16px]/[110%] font-semibold tracking-[-.16px] text-brown-1 lg:text-[20px]/[110%] lg:tracking-[-.2px]"
							>{benefit.label}</strong
						>
						<div class="text-[14px]/[160%] text-brown-4 lg:text-[16px]/[150%]">
							<SbRichtext text={benefit.description} />
						</div>
					</li>
				{/each}
			</ul>
		</div>
		<div
			use:slideIn={{ delay: 200, threshold: 0.1 }}
			class="col-span-full min-h-[100px] lg:col-start-9 lg:col-end-[17]"
		>
			<Image
				src={content.image.filename}
				alt={content.image.alt}
				title={content.image.title}
				class="pointer-events-none object-cover object-top lg:mt-auto lg:h-full lg:w-full lg:pt-32"
				height={700}
				sizes={{ 1024: { height: 1400 } }}
			/>
		</div>
	</PageGrid16>
</section>
